<template>
  <div class="dashboard-control-bar">
    <div class="control-wrap">
      <div class="date-control" :class="{ disabled: disabled}">
        <div class="now-date" ref="segmentGuideTarget" @click.stop="toggleRangePicker">
          <div class="date-picker">
            <div class="periods-label">
              {{ dateRangeStr }}
              <i class="icon icon--calendar-green"></i>
            </div>
            <transition name="calendar">
              <date-range-picker
                v-if="showDateRangePicker"
                :dateRange="dateRange"
                :maxDays="184"
                :minDate="calendarMinDate"
                :maxDate="calendarMaxDate"
                :timezone="timezone"
                :timezoneName="timezoneName"
                :showPaid="true"
                @onRangeCancel="showDateRangePicker = false"
                @onRangeChange="onRangeChange"
              />
            </transition>
          </div>
        </div>
      </div>
      <div class="campaign-control" :class="{ disabled: disabled || noData }">
        <div class="campaign-target" @click.stop="onSettingUtm(false)">
          <p class="title">{{ $t("journey.targeting.title") }}</p>
          <div class="switch">
            <span
              @mouseenter="utmToggleColor = '#bbbbbb'"
              @mouseleave="utmToggleColor = '#d7d7d7'"
            >
              <ba-switch
                :value="utmEnabled || surveyEnabled"
                :default="false"
                :onColor="'#3eaa08'"
                :offColor="utmToggleColor"
                :onLabel="''"
                :offLabel="''"
                :width="30"
                :size="'xsmall'"
              />
            </span>
          </div>
        </div>
        <div class="campaign-setting" v-show="utmEnabled || surveyEnabled">
          <i
            class="icon icon--setting-size16"
            @click.prevent="onSettingUtm(true)"
          ></i>
        </div>
      </div>
      <div class="device-control" :class="{ all: deviceAll, disabled: disabled || noData }">
        <ba-button-select
          :items="deviceList"
          :on="journeyOptions.device"
          :all="deviceAll"
          @change="(device) => onSetDevice(device)"
        />
        <button class="select-all-btn" @click="onSetDevice(null)">
          <i v-if="deviceAll" class="icon icon-chain-white size-16"></i>
          <i v-else class="icon icon-chain-red size-16"></i>
        </button>
      </div>
      <referrer-control
        :type="referrerType"
        :refer="journeyOptions.refer"
        :referrers="referrers"
        :ai-referrer-providers="aiReferrerProviders"
        :selected-ai-referer-ids="selectedAiRefererIds"
        :disabled="disabled || noData || deviceNoData"
        :select-width="142"
        :drop-width="300"
        :is-dashboard="true"
        @type-change="onReferrerTypeChange"
        @refer-change="onSetRefer"
        @ai-apply="onSetAiReferers"
      />
      <div class="source-control" :class="{ disabled: disabled || noData || deviceNoData }">
        <ui-checkbox
          v-for="exist in existOptions"
          :key="exist"
          :value="existStatus === exist || existStatus === 'all'"
          @input="onSetExist(exist)"
        >
          {{ $t(`common.${exist}`) }}
        </ui-checkbox>
      </div>
    </div>
    <div class="granularity-control" :class="{ disabled: disabled || noData || deviceNoData || segmentNoData }">
      <button
        v-for="option of granularityOptions"
        :key="`chartGranularity-${option}`"
        :class="[option, { selected: journeyOptions.granularity === option }]"
        @click="onGranularityChange(option)"
      >
        {{ $t("common.unit." + option) }}
      </button>
    </div>
    <dimmed v-if="settingUtm">
      <marketing
        :utmCache="utmCache"
        :tag="settingUtm"
        :sessionCount="latestSessionCount"
        :sessionTotalCount="latestSessionTotalCount"
        @onSetUtm="onSetUtm"
        @onSetSurvey="onSetSurvey"
        @onCloseLayer="settingUtm = false"
      />
    </dimmed>
    <div
      v-if="showSegmentGuide"
      class="segment-guide-overlay"
      @mousedown.prevent.stop="hideSegmentGuide"
      @click.prevent.stop
    >
      <div
        class="segment-guide-hole"
        :style="segmentGuideHoleStyle"
      ></div>
      <i
        class="icon icon--dashboard-blink segment-guide-blink"
        :style="segmentGuideBlinkStyle"
      ></i>
      <div
        class="segment-guide-text"
        :style="segmentGuideTooltipStyle"
        v-html="$t('dashboard.ai.segmentGuide')"
      >
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { parse, format } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { utcToLocalTimezone, getPercentage } from '@/libs/utils'
import { DEFAULT_TIMEZONE, DEFAULT_TIMEZONE_NAME } from '@/libs/constants'
import { getAiReferer, getBasicInfo, getTopRefer } from '@/api/journey'
import DateRangePicker from '@/components/ui/ba/DateRangePicker'
import BaButtonSelect from '@/components/ui/ba/ButtonSelect'
import BaSwitch from '@/components/ui/ba/Switch'
import UiCheckbox from '@/components/ui/UiCheckbox'
import Marketing from '@/components/journey/Marketing'
import Dimmed from '@/components/ui/Dimmed'
import ReferrerControl from '@/components/report/ReferrerControl.vue'

export default {
  name: 'DashboardControlBar',
  components: {
    DateRangePicker,
    BaButtonSelect,
    BaSwitch,
    UiCheckbox,
    Marketing,
    Dimmed,
    ReferrerControl
  },
  props: {
    domain: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    initialJourneyOptions: {
      type: Object,
      default: () => ({})
    },
    initialNoData: {
      type: Boolean,
      default: null
    }
  },
  data () {
    return {
      basicInfo: {},
      showDateRangePicker: false,
      deviceOptions: [
        {
          name: 'desktop',
          icon: 'icon--device desktop',
          iconHover: 'icon--device desktop hover',
          iconActive: 'icon--device desktop on',
          colorActive: '#EC0047',
          value: 'desktop'
        },
        {
          name: 'tablet',
          icon: 'icon--device tablet',
          iconHover: 'icon--device tablet hover',
          iconActive: 'icon--device tablet on',
          colorActive: '#EC0047',
          value: 'tablet'
        },
        {
          name: 'phone',
          icon: 'icon--device phone',
          iconHover: 'icon--device phone hover',
          iconActive: 'icon--device phone on',
          colorActive: '#EC0047',
          value: 'phone'
        }
      ],
      referrerType: 'general',
      referrerItems: {},
      aiReferrerProviders: [],
      existOptions: ['new', 'return'],
      granularityOptions: ['month', 'week', 'day', 'hour'],
      settingUtm: false,
      utmCache: null,
      utmToggleColor: '#d7d7d7',
      latestSessionCount: {},
      latestSessionTotalCount: 0,
      noData: null,
      deviceNoData: false,
      segmentNoData: false,
      showSegmentGuide: false,
      segmentGuideRect: null,
      segmentGuideExcludeRect: null
    }
  },
  computed: {
    ...mapState(['journeyPeriod', 'journeyOptions']),
    dateRange () {
      return {
        startDate: parse(this.journeyPeriod.start),
        endDate: parse(this.journeyPeriod.end)
      }
    },
    dateRangeStr () {
      return `${format(this.dateRange.startDate, 'MMM DD, YYYY')} - ${format(
        this.dateRange.endDate,
        'MMM DD, YYYY'
      )}`
    },
    calendarMinDate () {
      if (this.journeyPeriod.min) {
        return parse(this.journeyPeriod.min)
      } else {
        return null
      }
    },
    calendarMaxDate () {
      if (this.journeyPeriod.max) {
        return parse(this.journeyPeriod.max)
      } else {
        return utcToLocalTimezone(new Date(), this.timezoneName)
      }
    },
    deviceAll () {
      return !this.journeyOptions.device
    },
    deviceList () {
      return this.deviceOptions.map((option) => {
        return {
          ...option,
          hideLabel: this.deviceAll
        }
      })
    },
    initialReferrerItems () {
      return {
        desktop: [{ value: null, text: this.$t('common.all') }],
        tablet: [{ value: null, text: this.$t('common.all') }],
        phone: [{ value: null, text: this.$t('common.all') }],
        all: [{ value: null, text: this.$t('common.all') }]
      }
    },
    referrers () {
      const refs = this.referrerItems[this.journeyOptions.device || 'all']

      return refs.map((item) => {
        return {
          ...item,
          text: item.value === null ? this.$t('common.all') : decodeURI(item.text)
        }
      })
    },
    selectedAiRefererIds () {
      return new Set(this.journeyOptions.aiRefererIds || [])
    },
    existStatus () {
      if (this.journeyOptions.exist === null) {
        return 'all'
      } else if (this.journeyOptions.exist) {
        return 'return'
      } else {
        return 'new'
      }
    },
    timezone () {
      return (this.domain && this.domain.timezone) || DEFAULT_TIMEZONE
    },
    timezoneName () {
      return (this.domain && this.domain.timezoneName) || DEFAULT_TIMEZONE_NAME
    },
    utmEnabled () {
      return !!this.journeyOptions.utm
    },
    surveyEnabled () {
      return !!this.journeyOptions.survey
    },
    segmentGuideBlinkStyle () {
      if (!this.segmentGuideRect) return {}

      return {
        top: `${this.segmentGuideRect.top}px`,
        left: `${this.segmentGuideRect.left + (this.segmentGuideRect.width / 2)}px`,
        transform: 'translate(-50%, -20%)'
      }
    },
    segmentGuideTooltipStyle () {
      if (!this.segmentGuideRect) return {}

      return {
        top: `${this.segmentGuideRect.top + this.segmentGuideRect.height + 30}px`,
        left: `${this.segmentGuideRect.left - 4}px`,
        transform: 'translate(0, 0)'
      }
    },
    segmentGuideHoleStyle () {
      if (!this.segmentGuideExcludeRect) {
        return {
          boxShadow: '0 0 0 9999px rgba(12, 18, 28, 0.56)'
        }
      }

      return {
        top: `${this.segmentGuideExcludeRect.top}px`,
        left: `${this.segmentGuideExcludeRect.left}px`,
        width: `${this.segmentGuideExcludeRect.width}px`,
        height: `${this.segmentGuideExcludeRect.height}px`
      }
    }
  },
  watch: {
    initialNoData: {
      immediate: true,
      handler (value) {
        if (value !== null) {
          this.noData = value
          this.$emit('resetInitialNoData')
        }
      }
    },
    domain () {
      this.noData = null
    },
    journeyPeriod: {
      handler () {
        this.loadBasicInfo()
        this.utmCache = null
      }
    },
    'journeyOptions.aiRefererIds': {
      immediate: true,
      handler (value) {
        if (value && value.length) {
          this.referrerType = 'ai'
        }
      }
    },
    'journeyOptions.refer': {
      immediate: true,
      handler (value) {
        if (value !== null) {
          this.referrerType = 'general'
        }
      }
    }
  },
  created () {
    EventBus.$on('segmentNoData', (value) => {
      this.segmentNoData = value
    })
    EventBus.$on('dashboardAi:showSegmentGuide', this.showGuideForSegment)
    EventBus.$on('dashboardAi:hideSegmentGuide', this.hideSegmentGuide)
    this.loadBasicInfo()
    this.referrerItems = JSON.parse(JSON.stringify(this.initialReferrerItems))
  },
  mounted () {
    window.addEventListener('resize', this.updateSegmentGuideRect)
    window.addEventListener('scroll', this.updateSegmentGuideRect, true)
    document.addEventListener('mousedown', this.onDocumentMouseDown, true)
  },
  beforeDestroy () {
    EventBus.$off('dashboardAi:showSegmentGuide', this.showGuideForSegment)
    EventBus.$off('dashboardAi:hideSegmentGuide', this.hideSegmentGuide)
    window.removeEventListener('resize', this.updateSegmentGuideRect)
    window.removeEventListener('scroll', this.updateSegmentGuideRect, true)
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
  },
  methods: {
    ...mapMutations(['setJourneyPeriod', 'setJourneyOptions']),
    showGuideForSegment () {
      if (this.disabled) return

      this.showSegmentGuide = true
      this.$nextTick(() => {
        this.updateSegmentGuideRect()
      })
    },
    hideSegmentGuide () {
      this.showSegmentGuide = false
    },
    onDocumentMouseDown (e) {
      if (!this.showSegmentGuide) return
      e.preventDefault()
      e.stopPropagation()
      this.hideSegmentGuide()
    },
    updateSegmentGuideRect () {
      if (!this.showSegmentGuide) return

      const target = this.$refs.segmentGuideTarget
      if (!target) return

      const rect = target.getBoundingClientRect()
      this.segmentGuideRect = {
        top: rect.top - 14,
        left: rect.left - 12,
        width: rect.width + 22,
        height: rect.height + 22
      }

      const excludeTarget = document.querySelector('.domain-analysis .report-box')
      if (!excludeTarget) {
        this.segmentGuideExcludeRect = null
        return
      }

      const excludeRect = excludeTarget.getBoundingClientRect()
      this.segmentGuideExcludeRect = {
        top: excludeRect.top,
        left: excludeRect.left,
        width: excludeRect.width,
        height: excludeRect.height
      }
    },
    loadBasicInfo () {
      this.deviceNoData = false
      if (this.disabled) return
      const params = {
        ...this.journeyOptions,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        device: null,
        refer: null,
        aiRefererIds: null,
        exist: null
      }
      getBasicInfo(params, true).then(res => {
        this.basicInfo = res
        if (res.total) {
          for (const [device, count] of Object.entries(res.total)) {
            const label = getPercentage(count, res.session).toFixed(1) + '%'
            const targetOption = this.deviceOptions.find(option => option.value === device)
            if (targetOption) {
              this.$set(targetOption, 'label', label)
            }
          }
        }
        if (!this.journeyOptions.utm && !this.journeyOptions.survey) {
          this.latestSessionCount = { ...res.total }
          this.latestSessionTotalCount = res.session
        }
        if (this.noData === null) {
          this.noData = !res || !res.session
        }
        this.getReferrerItems()
        this.getAiReferrerItems()
      })
    },
    getReferrerItems () {
      const requestDevice = this.journeyOptions.device
      const params = {
        ...this.journeyOptions,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        refer: null,
        exist: null,
        granularity: 'day'
      }
      delete params.aiRefererIds
      getTopRefer(params, true).then(res => {
        this.referrerItems = JSON.parse(JSON.stringify(this.initialReferrerItems))
        for (let item of res.total) {
          const [refer, session] = Object.entries(item)[0]
          let referText = refer
          if (refer.substr(0, 4) === 'http') {
            const referObj = document.createElement('a')
            referObj.href = refer
            referText = refer.substr(referObj.protocol.length + 2)
          }

          const totalSession = requestDevice ? this.basicInfo.total[requestDevice] : this.basicInfo.session
          const referData = {
            value: refer,
            text: referText,
            additionalText: `(${getPercentage(session, totalSession).toFixed(1)}%)`
          }

          this.referrerItems[requestDevice || 'all'].push(referData)
        }
      }).catch(() => {})
    },
    getAiReferrerItems () {
      const requestDevice = this.journeyOptions.device
      const params = {
        ...this.journeyOptions,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        device: requestDevice,
        aiRefererIds: null,
        granularity: 'day'
      }
      delete params.refer
      delete params.exist
      getAiReferer(params, true).then(res => {
        const totalSession = requestDevice ? this.basicInfo.total[requestDevice] : this.basicInfo.session

        this.aiReferrerProviders = (res.total || []).map(group => {
          return {
            ...group,
            additionalText: `${getPercentage(group.count, totalSession).toFixed(1)}%`,
            values: group.values.map(value => ({
              ...value,
              additionalText: `${getPercentage(value.count, totalSession).toFixed(1)}%`
            }))
          }
        })
      }).catch(() => {
        this.aiReferrerProviders = []
      })
    },
    toggleRangePicker () {
      this.showDateRangePicker = !this.showDateRangePicker
    },
    async onRangeChange (data) {
      const range = data.range
      const rangeStart = format(range.startDate, 'YYYY-MM-DD')
      const rangeEnd = format(range.endDate, 'YYYY-MM-DD')

      if (
        this.journeyPeriod.start !== rangeStart ||
        this.journeyPeriod.end !== rangeEnd
      ) {
        this.noData = null
        this.setJourneyPeriod({
          ...this.journeyPeriod,
          start: rangeStart,
          end: rangeEnd
        })
        this.setJourneyOptions({ ...this.initialJourneyOptions })
        this.referrerType = 'general'
        this.utmCache = null
      }
      this.showDateRangePicker = false
    },
    onSetDevice (value) {
      this.setJourneyOptions({
        ...this.journeyOptions,
        device: value,
        refer: this.initialJourneyOptions['refer'],
        aiRefererIds: this.initialJourneyOptions.aiRefererIds || null,
        exist: this.initialJourneyOptions['exist'],
        granularity: this.initialJourneyOptions['granularity']
      })
      this.referrerType = 'general'
      this.deviceNoData = this.deviceAll ? !this.basicInfo.session : !this.basicInfo.total[value]
      if (!this.deviceNoData) {
        this.getReferrerItems()
        this.getAiReferrerItems()
      }
    },
    onSetRefer (value) {
      this.setJourneyOptions({
        ...this.journeyOptions,
        refer: value,
        aiRefererIds: this.initialJourneyOptions.aiRefererIds || null,
        exist: this.initialJourneyOptions['exist'],
        granularity: this.initialJourneyOptions['granularity']
      })
    },
    onReferrerTypeChange (type) {
      if (this.referrerType === type) return

      this.referrerType = type

      if (type === 'ai' && this.journeyOptions.refer !== null) {
        this.setJourneyOptions({
          ...this.journeyOptions,
          refer: this.initialJourneyOptions['refer'],
          exist: this.initialJourneyOptions['exist'],
          granularity: this.initialJourneyOptions['granularity']
        })
      }

      if (type === 'general' && this.selectedAiRefererIds.size) {
        this.setJourneyOptions({
          ...this.journeyOptions,
          aiRefererIds: this.initialJourneyOptions.aiRefererIds || null,
          exist: this.initialJourneyOptions['exist'],
          granularity: this.initialJourneyOptions['granularity']
        })
      }
    },
    onSetAiReferers (aiRefererIds) {
      this.setJourneyOptions({
        ...this.journeyOptions,
        refer: this.initialJourneyOptions['refer'],
        aiRefererIds: aiRefererIds.length ? aiRefererIds : this.initialJourneyOptions.aiRefererIds || null,
        exist: this.initialJourneyOptions['exist'],
        granularity: this.initialJourneyOptions['granularity']
      })
    },
    onSetExist (value) {
      let newValue = null
      if (this.existStatus === 'all' || this.existStatus === value) {
        newValue = value === 'new'
      }
      this.setJourneyOptions({
        ...this.journeyOptions,
        exist: newValue,
        granularity: this.initialJourneyOptions['granularity']
      })
    },
    onGranularityChange (value) {
      this.setJourneyOptions({ ...this.journeyOptions, granularity: value })
    },
    onSettingUtm (force) {
      if ((this.utmEnabled || this.surveyEnabled) && !force) {
        this.setJourneyOptions({ ...this.initialJourneyOptions })
        this.referrerType = 'general'
        this.loadBasicInfo()
      } else {
        this.utmCache = this.utmCache || this.journeyOptions.utm
        this.settingUtm = true
      }
    },
    onSetUtm (selectedUtm) {
      this.utmCache = selectedUtm
      this.settingUtm = false
      if (
        JSON.stringify(this.journeyOptions.utm) !== JSON.stringify(selectedUtm)
      ) {
        this.setJourneyOptions({
          ...this.initialJourneyOptions,
          utm: selectedUtm
        })
        this.referrerType = 'general'
        this.loadBasicInfo()
      }
    },
    onSetSurvey (selectedSurvey) {
      this.utmCache = null
      this.setJourneyOptions({
        ...this.initialJourneyOptions,
        survey: selectedSurvey
      })
      this.referrerType = 'general'
      this.loadBasicInfo()
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-control-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #aaa;
  position: relative;

  .control-wrap {
    display: flex;
    align-items: center;
    gap: 16px;

    .date-control {
      .date-picker {
        margin: 0 0 0 4px;
        position: relative;
        border-radius: 8px;
        vertical-align: middle;
        display: inline-block;

        .periods-label {
          @include flexYcenter;
          gap: 5px;
          font-size: 16px;
          font-weight: 600;
          line-height: 100%;
          color: #0e90a5;
          transition: color 0.2s ease;
          cursor: pointer;
        }

        .icon--calendar-green {
          vertical-align: middle;
          pointer-events: none;
          padding: 2px;
          border-radius: 3px;
          border: solid 1px transparent;
          background-size: 16px 16px;
          transition: border-color 0.2s ease;
        }

        &:hover {
          .periods-label {
            color: #035e6d;
          }

          .icon--calendar-green {
            border-color: #d9d9d9;
          }
        }

        .date_range_picker {
          top: 26px;
          left: -4px;
          margin: 0;
          transform: none;
        }
      }

      .now-date {
        .date-picker {
          border-color: #e0e0e0;

          input {
            font-weight: 500;
            color: #0e90a5;
          }
        }
      }

      &.disabled {
        pointer-events: none;
        .date-picker {
          .periods-label {
            color: #777;
            filter: grayscale(1);
          }
        }
      }
    }

    .campaign-control {
      @include flexYcenter;
      gap: 4px;
      .campaign-target {
        @include flexYcenter;
        gap: 7px;
        cursor: pointer;
        .title {
          color: #444;
          font-size: 12px;
          font-weight: 500;
        }
      }
      .campaign-setting {
        display: flex;
        cursor: pointer;
        .icon--setting-size16 {
          border-radius: 11px;
          &:hover {
            background-color: rgba(0, 0, 0, 0.1);
          }
        }
      }
      &.disabled {
        pointer-events: none;
        .campaign-target {
          .title {
            color: #aaa;
            .switch {
              opacity: 0.3;
            }
          }
        }
      }
    }

    .device-control {
      position: relative;
      margin-right: 15px;
      display: inline-block;
      vertical-align: middle;
      transition: transform 0.3s ease;

      .select-all-btn {
        position: absolute;
        right: -17px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background-color: #fff;
        background-clip: padding-box;
        border-radius: 100%;
        border: 1px solid #00000033;
        box-shadow: 0px 5px 6px -2px rgba(0, 0, 0, 0.16);
        z-index: 1;

        &:hover {
          background-color: #f4f4f4;
        }
      }

      & /deep/ {
        .ba-button-select {
          border-radius: 7px;
        }

        .ba-button {
          button {
            height: 30px;
            padding: 7px 5px 7px 8px;
            margin-left: -1px;
            margin-right: 0px;
            background-color: transparent;
            z-index: auto;

            .icon {
              top: 7px;
            }

            .text {
              min-width: 40px;
              margin-left: 20px;
              margin-top: 0;
              font-size: 12px;
              line-height: 1;
            }
            &.active {
              z-index: 1;
            }
          }

          &:first-child {
            button {
              border-top-left-radius: 7px;
              border-bottom-left-radius: 7px;
              margin-left: 0px;
            }
          }

          &:last-child {
            button {
              border-top-right-radius: 7px;
              border-bottom-right-radius: 7px;
              .icon {
                left: 10px;
              }
            }
          }
        }
      }

      &.all {
        .select-all-btn {
          border-color: #ffffffcc;
          background-color: #3eaa08;
        }

        & /deep/ .ba-button {
          button {
            min-width: 73px;
            .icon {
              left: 50%;
              transform: translateX(-50%);
            }
          }
        }
      }
      &.disabled {
        pointer-events: none;
        opacity: 0.3;
        & /deep/ {
          .ba-button-select {
            background-color: #888;
          }
        }
      }
    }

    .source-control {
      .ui_checkbox {
        & /deep/ {
          .text {
            margin-left: 26px;
            font-size: 13px;
          }
        }
        &:nth-last-of-type(1) {
          margin-left: 16px;
        }
      }
      &.disabled {
        pointer-events: none;
        .ui_checkbox {
          & /deep/ {
            .icon {
              filter: grayscale(100%);
              opacity: 0.27;
            }
            .text {
              color: #aaa;
            }
          }
        }
      }
    }
  }
  .granularity-control {
    height: 30px;
    border-radius: 5px;
    box-sizing: border-box;
    button {
      height: 100%;
      width: 29px;
      color: #444;
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      transition: color 0.2s ease, background-color 0.2s ease;
      border-top: 1px solid #bbb;
      border-bottom: 1px solid #bbb;
      background-color: #fff;
      &:first-child {
        width: 30px;
        border-left: 1px solid #bbb;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }
      &:last-child {
        border-right: 1px solid #bbb;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
      &:not(:last-child) {
        border-right: 1px solid #bbb;
      }
      &.selected {
        position: relative;
        background: #444;
        color: #fff;
        border-color: #444;
        &:not(:first-child) {
          &:before {
            content: "";
            position: absolute;
            top: -1px;
            left: -1px;
            width: 1px;
            height: calc(100% + 2px);
            background: #444;
          }
        }
      }
    }
    &.disabled {
      pointer-events: none;
      opacity: 0.3;
    }
  }

  .segment-guide-overlay {
    position: fixed;
    inset: 0;
    z-index: 2000;
    pointer-events: none;
  }

  .segment-guide-hole {
    position: fixed;
    border-radius: 10px;
    background: transparent;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.68);
    pointer-events: none;
  }

  .segment-guide-blink {
    position: fixed;
    pointer-events: none;
  }

  .segment-guide-text {
    position: fixed;
    width: 500px;
    color: #ffffff;
    font-size: 18px;
    line-height: 1.45;
    pointer-events: none;
    text-align: left;
    text-shadow: 0 2px 20px rgba(0, 0, 0, 0.35);
  }
}
</style>
